import React from 'react';
import styled from 'styled-components';
import { MdDesignServices, MdPrint, MdBrush, MdInventory, MdDiamond, MdSearch, MdSchedule, MdEmojiEvents } from 'react-icons/md';

const ProcessContainer = styled.section`
  padding: 5rem 2rem 4rem;
  background: 
    radial-gradient(circle at 25% 15%, rgba(212, 175, 55, 0.012) 0%, transparent 50%),
    radial-gradient(circle at 75% 85%, rgba(212, 175, 55, 0.008) 0%, transparent 50%),
    radial-gradient(circle at 50% 50%, rgba(212, 175, 55, 0.004) 0%, transparent 70%),
    linear-gradient(135deg, var(--color-obsidian) 0%, var(--color-charcoal) 20%, var(--color-obsidian) 40%, var(--color-charcoal) 60%, var(--color-obsidian) 80%, var(--color-charcoal) 100%);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 15% 25%, rgba(212, 175, 55, 0.015) 0%, transparent 60%),
      radial-gradient(circle at 85% 75%, rgba(212, 175, 55, 0.012) 0%, transparent 60%),
      radial-gradient(circle at 40% 10%, rgba(212, 175, 55, 0.008) 0%, transparent 70%),
      radial-gradient(circle at 60% 90%, rgba(212, 175, 55, 0.006) 0%, transparent 70%);
    pointer-events: none;
    z-index: 1;
    animation: luxury-ambient-glow 40s ease-in-out infinite;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 20%;
    right: 5%;
    width: 500px;
    height: 500px;
    background: radial-gradient(circle at center, rgba(212, 175, 55, 0.006) 0%, rgba(212, 175, 55, 0.003) 40%, transparent 70%);
    border-radius: 50%;
    filter: blur(8rem);
    pointer-events: none;
    animation: luxury-float 20s ease-in-out infinite;
    z-index: 1;
  }
  
  @media (max-width: 968px) {
    padding: 4rem 1.5rem 3rem;
  }
  
  @media (max-width: 768px) {
    padding: 3rem 1rem 2.5rem;
    
    &::after {
      width: 200px;
      height: 200px;
      top: 10%;
      right: -15%;
    }
  }
`;

const ProcessContent = styled.div`
  max-width: 1600px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
  
  @media (max-width: 1400px) {
    max-width: 1200px;
  }
  
  @media (max-width: 968px) {
    max-width: 900px;
  }
  
  @media (max-width: 768px) {
    max-width: 95%;
  }
`;

const SectionTitle = styled.h2`
  font-size: clamp(2rem, 4.5vw, 3.25rem);
  text-align: center;
  margin-bottom: 2rem;
  color: var(--color-pearl);
  font-weight: 950;
  letter-spacing: -0.05em;
  line-height: 1.05;
  position: relative;
  font-family: 'Inter', sans-serif;
  text-shadow: 
    0 6px 12px rgba(11, 11, 11, 0.9),
    0 0 60px rgba(212, 175, 55, 0.15),
    0 0 120px rgba(212, 175, 55, 0.08);
  animation: luxury-glow 10s ease-in-out infinite;
  
  &::before {
    content: '';
    position: absolute;
    top: -30px;
    left: -30px;
    right: -30px;
    bottom: -30px;
    background: 
      radial-gradient(circle at 25% 25%, rgba(212, 175, 55, 0.004) 0%, transparent 70%),
      radial-gradient(circle at 75% 75%, rgba(212, 175, 55, 0.003) 0%, transparent 70%);
    filter: blur(3rem);
    z-index: -1;
    animation: luxury-text-aura 15s ease-in-out infinite;
  }
  
  span {
    background: linear-gradient(135deg, #D4AF37 0%, #F8F8F8 15%, #E8B86D 30%, #F8F8F8 45%, #D4AF37 60%, #E8B86D 75%, #F8F8F8 90%, #D4AF37 100%);
    background-size: 500% 100%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: luxury-text-shimmer 12s ease-in-out infinite;
    position: relative;
    display: inline-block;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -2rem;
    left: 50%;
    transform: translateX(-50%);
    width: 150px;
    height: 4px;
    background: linear-gradient(90deg, transparent, var(--color-champagne), var(--color-rose-gold), var(--color-champagne), transparent);
    opacity: 0.9;
    border-radius: 2px;
    animation: luxury-divider-pulse 6s ease-in-out infinite;
  }
  
  @media (max-width: 968px) {
    margin-bottom: 1.75rem;
    
    &::after {
      width: 100px;
      height: 3px;
      bottom: -1rem;
    }
  }
  
  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
    
    &::after {
      width: 100px;
      height: 2px;
      bottom: -1rem;
    }
  }
`;

const SectionSubtitle = styled.p`
  text-align: center;
  max-width: 700px;
  margin: 0 auto 3rem;
  font-size: clamp(1rem, 2vw, 1.125rem);
  color: var(--color-slate-300);
  line-height: 1.5;
  font-weight: 400;
  opacity: 0.95;
  letter-spacing: 0.015em;
  text-shadow: 0 2px 4px rgba(11, 11, 11, 0.6);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: -12px;
    left: -20px;
    right: -20px;
    bottom: -12px;
    background: rgba(212, 175, 55, 0.002);
    border-radius: 12px;
    filter: blur(1rem);
    z-index: -1;
    opacity: 0.8;
  }
  
  @media (max-width: 968px) {
    margin: 0 auto 2.5rem;
    max-width: 95%;
  }
  
  @media (max-width: 768px) {
    margin: 0 auto 2rem;
    line-height: 1.5;
  }
`;

const ProcessSteps = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  margin-bottom: 3rem;
  position: relative;
  padding: 2rem 0;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 20%, rgba(212, 175, 55, 0.015) 0%, transparent 70%),
      radial-gradient(circle at 80% 80%, rgba(212, 175, 55, 0.012) 0%, transparent 70%);
    border-radius: 24px;
    border: 1px solid rgba(212, 175, 55, 0.04);
    backdrop-filter: blur(20px);
    z-index: 1;
    opacity: 0.9;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 5%;
    right: 5%;
    height: 3px;
    background: linear-gradient(90deg, transparent, rgba(212, 175, 55, 0.2), var(--color-champagne), rgba(212, 175, 55, 0.2), transparent);
    z-index: 1;
    animation: luxury-divider-pulse 8s ease-in-out infinite;
  }
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1.75rem;
  }
  
  @media (max-width: 968px) {
    gap: 1.5rem;
    margin-bottom: 2.5rem;
    padding: 1.5rem 0;
    grid-template-columns: 1fr;
    
    &::after {
      display: none;
    }
  }
  
  @media (max-width: 768px) {
    gap: 1.25rem;
    margin-bottom: 2rem;
    padding: 1rem 0;
  }
  
  @media (max-width: 576px) {
    gap: 1rem;
    margin-bottom: 1.75rem;
    padding: 0.75rem 0;
    grid-template-columns: 1fr;
  }
  
  @media (max-width: 480px) {
    gap: 0.875rem;
    margin-bottom: 1.5rem;
    padding: 0.5rem 0;
  }
  
  @media (max-width: 375px) {
    gap: 0.75rem;
    margin-bottom: 1.25rem;
    padding: 0.375rem 0;
  }
  
  @media (max-width: 320px) {
    gap: 0.625rem;
    margin-bottom: 1rem;
    padding: 0.25rem 0;
  }
`;

const StepCard = styled.div`
  background: 
    radial-gradient(circle at 30% 30%, rgba(212, 175, 55, 0.05) 0%, transparent 70%),
    radial-gradient(circle at 70% 70%, rgba(212, 175, 55, 0.03) 0%, transparent 70%),
    linear-gradient(135deg, rgba(212, 175, 55, 0.02) 0%, transparent 50%, rgba(212, 175, 55, 0.015) 100%);
  border: 1px solid rgba(212, 175, 55, 0.15);
  border-radius: 16px;
  padding: 2rem 1.5rem;
  text-align: center;
  position: relative;
  transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(24px);
  z-index: 2;
  overflow: hidden;
  box-shadow: 
    0 12px 40px rgba(0, 0, 0, 0.15),
    0 0 0 1px rgba(212, 175, 55, 0.1),
    inset 0 1px 0 rgba(212, 175, 55, 0.15);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(212, 175, 55, 0.03) 0%, transparent 40%, rgba(212, 175, 55, 0.02) 100%);
    border-radius: 24px;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.8s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(45deg, transparent, rgba(212, 175, 55, 0.1), transparent);
    border-radius: 24px;
    z-index: -1;
    opacity: 0;
    transition: opacity 0.8s ease;
  }
  
  &:hover {
    transform: translateY(-16px) scale(1.05);
    box-shadow: 
      0 40px 80px rgba(212, 175, 55, 0.2),
      0 0 60px rgba(212, 175, 55, 0.1),
      0 0 0 1px rgba(212, 175, 55, 0.25),
      inset 0 1px 0 rgba(212, 175, 55, 0.3);
    border-color: rgba(212, 175, 55, 0.3);
    background: 
      radial-gradient(circle at 30% 30%, rgba(212, 175, 55, 0.08) 0%, transparent 70%),
      radial-gradient(circle at 70% 70%, rgba(212, 175, 55, 0.06) 0%, transparent 70%),
      linear-gradient(135deg, rgba(212, 175, 55, 0.04) 0%, transparent 50%, rgba(212, 175, 55, 0.03) 100%);
    
    &::before,
    &::after {
      opacity: 1;
    }
  }
  
  @media (max-width: 768px) {
    padding: 3rem 2rem;
  }
  
  @media (max-width: 480px) {
    padding: 2.5rem 1.5rem;
  }
`;

const StepNumber = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, var(--color-champagne) 0%, var(--color-rose-gold) 50%, var(--color-champagne) 100%);
  background-size: 200% 100%;
  color: var(--color-obsidian);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: clamp(1.6rem, 2.5vw, 2rem);
  font-weight: 900;
  margin: 0 auto 2.5rem;
  position: relative;
  box-shadow: 
    0 12px 30px rgba(212, 175, 55, 0.3),
    0 0 0 2px rgba(212, 175, 55, 0.2),
    inset 0 2px 4px rgba(255, 255, 255, 0.3);
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  animation: luxury-text-shimmer 8s ease-in-out infinite;
  
  &::before {
    content: '';
    position: absolute;
    top: -3px;
    left: -3px;
    right: -3px;
    bottom: -3px;
    background: linear-gradient(135deg, var(--color-champagne), var(--color-rose-gold));
    border-radius: 50%;
    z-index: -1;
    opacity: 0;
    transition: all 0.6s ease;
    filter: blur(8px);
  }
  
  ${StepCard}:hover & {
    transform: scale(1.1) rotateY(360deg);
    box-shadow: 
      0 20px 40px rgba(212, 175, 55, 0.4),
      0 0 0 3px rgba(212, 175, 55, 0.3),
      inset 0 2px 4px rgba(255, 255, 255, 0.5);
    background-position: 100% 0;
    
    &::before {
      opacity: 0.5;
    }
  }
  
  @media (max-width: 768px) {
    width: 70px;
    height: 70px;
    margin: 0 auto 2rem;
  }
  
  @media (max-width: 480px) {
    width: 60px;
    height: 60px;
    margin: 0 auto 1.5rem;
  }
`;

const StepIcon = styled.div`
  font-size: 3.5rem;
  margin-bottom: 2rem;
  background: linear-gradient(135deg, var(--color-champagne) 0%, var(--color-rose-gold) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  opacity: 0.9;
  filter: drop-shadow(0 2px 4px rgba(212, 175, 55, 0.1));
`;

const StepTitle = styled.h3`
  font-size: 1.6rem;
  color: var(--color-pearl);
  margin-bottom: 1.5rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.3;
  text-shadow: 0 1px 2px rgba(11, 11, 11, 0.8);
  
  span {
    background: linear-gradient(135deg, var(--color-champagne) 0%, var(--color-rose-gold) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const StepDescription = styled.p`
  color: var(--color-slate-300);
  line-height: 1.7;
  font-size: 1.05rem;
  font-weight: 400;
  opacity: 0.95;
  letter-spacing: 0.005em;
`;

const QualitySection = styled.div`
  background: rgba(212, 175, 55, 0.03);
  border: 1px solid rgba(212, 175, 55, 0.15);
  border-radius: 24px;
  padding: 5rem 3rem;
  text-align: center;
  margin-top: 6rem;
  position: relative;
  backdrop-filter: blur(20px);
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 30% 30%, rgba(212, 175, 55, 0.02) 0%, transparent 50%),
      radial-gradient(circle at 70% 70%, rgba(212, 175, 55, 0.015) 0%, transparent 50%);
    pointer-events: none;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: -1px;
    left: -1px;
    right: -1px;
    bottom: -1px;
    background: linear-gradient(45deg, transparent, rgba(212, 175, 55, 0.1), transparent, rgba(212, 175, 55, 0.05), transparent);
    border-radius: 24px;
    z-index: -1;
  }
`;

const QualityTitle = styled.h3`
  font-size: 2.5rem;
  color: var(--color-pearl);
  margin-bottom: 2rem;
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1.2;
  text-shadow: 0 1px 2px rgba(11, 11, 11, 0.8);
  position: relative;
  z-index: 2;
  
  span {
    background: linear-gradient(135deg, var(--color-champagne) 0%, var(--color-rose-gold) 50%, var(--color-champagne) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const QualityText = styled.p`
  font-size: 1.3rem;
  color: var(--color-slate-300);
  line-height: 1.7;
  max-width: 800px;
  margin: 0 auto 3rem;
  font-weight: 400;
  opacity: 0.95;
  letter-spacing: 0.01em;
  position: relative;
  z-index: 2;
`;

const QualityFeatures = styled.div`
  display: flex;
  justify-content: center;
  gap: 3rem;
  margin-top: 3rem;
  flex-wrap: wrap;
  position: relative;
  z-index: 2;
`;

const QualityFeature = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: rgba(212, 175, 55, 0.05);
  padding: 1rem 1.5rem;
  border-radius: 12px;
  border: 1px solid rgba(212, 175, 55, 0.1);
  backdrop-filter: blur(8px);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  
  .icon {
    font-size: 1.2rem;
    background: linear-gradient(135deg, var(--color-champagne) 0%, var(--color-rose-gold) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  span {
    color: var(--color-pearl);
    font-weight: 500;
    font-size: 1rem;
    letter-spacing: 0.01em;
  }
  
  &:hover {
    transform: translateY(-3px);
    background: rgba(212, 175, 55, 0.08);
    border-color: rgba(212, 175, 55, 0.2);
    box-shadow: 0 8px 20px rgba(212, 175, 55, 0.1);
  }
`;

const processSteps = [
  {
    number: 1,
    icon: MdDesignServices,
    title: "Concept & Design",
    description: "Our artists study every detail of the character, creating detailed sketches and 3D models that capture their essence perfectly."
  },
  {
    number: 2,
    icon: MdPrint,
    title: "Sculpting & Molding", 
    description: "Master sculptors handcraft each figure using premium materials, ensuring every muscle, expression, and detail is authentic."
  },
  {
    number: 3,
    icon: MdBrush,
    title: "Painting & Finishing",
    description: "Expert painters apply multiple layers of paint, weathering effects, and final touches to bring the character to life."
  },
  {
    number: 4,
    icon: MdInventory,
    title: "Quality & Packaging",
    description: "Each figure undergoes rigorous quality checks before being packaged in premium collector-grade boxes."
  }
];

const Process: React.FC = () => {
  return (
    <ProcessContainer id="process">
      <ProcessContent>
        <SectionTitle>OUR <span>CRAFT PROCESS</span></SectionTitle>
        <SectionSubtitle>
          Discover the meticulous artistry behind every legendary figure we create
        </SectionSubtitle>
        
        <ProcessSteps>
          {processSteps.map((step) => (
            <StepCard key={step.number}>
              <StepNumber>{step.number}</StepNumber>
              <StepIcon>{React.createElement(step.icon as React.ComponentType<any>)}</StepIcon>
              <StepTitle>{step.title}</StepTitle>
              <StepDescription>{step.description}</StepDescription>
            </StepCard>
          ))}
        </ProcessSteps>
        
        <QualitySection>
          <QualityTitle>Uncompromising <span>Quality Standards</span></QualityTitle>
          <QualityText>
            Every KIII Toys Studios figure is a testament to our commitment to excellence. 
            We use only the finest materials and employ traditional artisan techniques 
            combined with modern precision to create collectibles that stand the test of time.
          </QualityText>
          <QualityFeatures>
            <QualityFeature>
              {React.createElement(MdDiamond as React.ComponentType<any>, { style: { marginRight: '0.5rem', color: 'var(--color-champagne)' } })}
              Premium Materials
            </QualityFeature>
            <QualityFeature>
              {React.createElement(MdSearch as React.ComponentType<any>, { style: { marginRight: '0.5rem', color: 'var(--color-champagne)' } })}
              Detail Precision
            </QualityFeature>
            <QualityFeature>
              {React.createElement(MdSchedule as React.ComponentType<any>, { style: { marginRight: '0.5rem', color: 'var(--color-champagne)' } })}
              Lifetime Durability
            </QualityFeature>
            <QualityFeature>
              {React.createElement(MdEmojiEvents as React.ComponentType<any>, { style: { marginRight: '0.5rem', color: 'var(--color-champagne)' } })}
              Limited Editions
            </QualityFeature>
          </QualityFeatures>
        </QualitySection>
      </ProcessContent>
    </ProcessContainer>
  );
};

export default Process;